import React, { useEffect, useMemo, useRef, useState } from 'react';
import useFocusTrap from '../../../hooks/useFocusTrap';
import HoverTooltip from '../../shared/HoverTooltip';
import {
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import { useActiveRole } from '../../../hooks/useActiveRole';
import {
  getAimGroundTruth, updateAimGroundTruth,
  getAimHallucinations, getAimGroundTruthChanges, getAimCategoryConfusion,
  getAimConsensusDetail,
} from '../../../api/auth';
import {
  useAimSnapshot, useAimKnowledgeRecencyTimeline,
} from '../lib/aimQueries';
import { useNotification } from '../../../contexts/NotificationContext';
import ProviderColorDot from '../primitives/ProviderColorDot';
import { prettifyProviderLabel } from '../lib/providerColors';
import AimEmpty, { AimLoading, AimError } from '../primitives/AimEmpty';
import InsightCard from '../primitives/InsightCard';
import InfoTooltip from '../primitives/InfoTooltip';
import { buildHallucinationWarning } from '../lib/insights';
import { classifyHallucinationType, HALLUCINATION_TYPE_LABELS } from '../lib/hallucinationType';
import { prettifyCategory, prettifyAmenityLabel } from '../lib/categoryLabel';
import { toEpoch, knowledgeRecencyView } from '../lib/knowledgeRecency';
import AimPagination from '../primitives/AimPagination';
import usePagedRows from '../lib/usePagedRows';
import { buildPageRange } from '../lib/pagination';
import {
  isLikelyTypoEdit,
  canonicalizeFactValue,
  isTypeMismatch,
  isMinMaxConfusion,
  prettifyBooleanValue,
} from '../lib/factFlags';
import {
  formatDaysBehind,
  confusionTier,
  categoryConfusionStaleness,
  categoryConfusionStaleDetail,
  consensusDisagreements,
  consensusProviderPartition,
  consensusPartitionLabel,
} from '../lib/diagnosticsFormat';
import { TABLE_FRAME, TABLE_FRAME_FLUSH, TABLE_THEAD, TABLE_ROW, TABLE_ROW_CLICKABLE, clickableRowProps } from '../../shared/tableStyles';

// 6C Providers bar — the four buckets of `consensusProviderPartition`, in the
// panel's colour convention: green good, amber warning, red error, grey neutral.
// The grey slot used to hold a "silent" segment the RPC never fills, dropped as
// dead; `unscored` is the real thing that belongs in it.
// Boundary between two touching segments, in the card's own background colour
// so it reads as a gap rather than a fifth colour. Adjacent buckets are
// separated by hue alone — emerald-500 and gray-400 have *identical* relative
// luminance (1.00:1), and agreed|mixed is 1.18:1 — so in greyscale, in print,
// or to a deuteranopic reader the tripadvisor_award bar (half agreed, half
// unscored) collapses into one solid block. The divider restores the boundary
// without touching the palette: worst case it is 2.15:1 against amber on the
// white card and 3.67:1 against grey on the dark one.
//
// Applied only from the second rendered segment onward, so a bar with one
// bucket has no hairline and an empty bucket leaves no artifact.
const SEGMENT_DIVIDER = 'border-l border-white dark:border-gray-900';

// Consensus at or above this is not flagged. Matches the "below 50%" wording in
// the panel's own description, so the copy and the badge cannot drift apart.
const CONSENSUS_FLAG_THRESHOLD = 50;

// Two severities, so the column distinguishes "fix this first" from "worth a
// look" instead of being silent about everything that is not contact info.
const FACT_FLAGS = {
  priority: {
    key: 'priority',
    label: 'Priority',
    icon: '❗',
    className: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    reason: 'Guest-facing contact info the providers mostly disagree on — a wrong number or address sends guests to the wrong place, so fix this first.',
  },
  review: {
    key: 'review',
    label: 'Review',
    icon: '⚠',
    className: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    reason: 'The providers mostly disagree on this fact. Usually your public-facing information is inconsistent across sources rather than any one provider being wrong.',
  },
};

const CONSENSUS_SEGMENTS = [
  { key: 'agreed', label: 'Agreed', swatch: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'mixed', label: 'Mixed', swatch: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  { key: 'disagreed', label: 'Disagreed', swatch: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  { key: 'unscored', label: 'Unscored', swatch: 'bg-gray-400 dark:bg-gray-500', text: 'text-gray-500 dark:text-gray-400' },
];

/**
 * The 6C Providers cell: a partition bar over the providers that stated a fact,
 * plus the counts in words.
 *
 * The bar is deliberately NOT built from `providers_agreeing` /
 * `providers_disagreeing` — those are overlapping sets (a provider can be
 * accurate on one statement and inaccurate on another), and normalising over
 * their sum drew a 57.1% bar beside a 100% Consensus column on 7 of 56
 * production rows. `consensusProviderPartition` sorts each provider into
 * exactly one bucket instead, so the segments always sum to the whole.
 *
 * @param {object|null} partition from `consensusProviderPartition`, or null when
 *   the row carries no `providers[]`.
 * @param {number} statingCount the row's own `providers_stating`, used only by
 *   the no-partition branch — it is a different condition from an empty
 *   `providers[]`, so the fallback tooltip must not claim nobody stated the fact.
 */
function ProviderPartitionCell({ partition, statingCount }) {
  if (!partition) {
    return (
      <span
        className="text-xs tabular-nums text-gray-400 dark:text-gray-500 whitespace-nowrap"
        title={statingCount === 0
          ? 'No provider stated this fact in this run, so there is nothing to break down.'
          : 'Per-provider statements are not available for this run, so the breakdown cannot be drawn.'}
      >
        — {statingCount} stated
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-1 items-start max-[499px]:items-end">
      <div
        role="img"
        aria-label={consensusPartitionLabel(partition)}
        className="w-[160px] max-w-full flex h-2 rounded overflow-hidden bg-gray-100 dark:bg-gray-800"
      >
        {CONSENSUS_SEGMENTS.filter(({ key }) => partition[key] > 0)
          .map(({ key, label, swatch }, idx) => {
            const count = partition[key];
            // Proportional flex rather than percentage widths: the segments
            // divide the track exactly, so no rounding can push it past 100%.
            const pct = ((count / partition.stating) * 100).toFixed(1);
            return (
              <div
                key={key}
                className={idx > 0 ? `${swatch} ${SEGMENT_DIVIDER}` : swatch}
                style={{ flexGrow: count, flexBasis: 0 }}
                title={`${label}: ${count} of ${partition.stating} providers (${pct}%)`}
              />
            );
          })}
      </div>
      <span className="text-xs tabular-nums text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {CONSENSUS_SEGMENTS.filter(({ key }) => key !== 'unscored' || partition.unscored > 0)
          .map(({ key, label, text }, idx) => (
            <React.Fragment key={key}>
              {idx > 0 && ' · '}
              {/* An empty bucket is muted: a red "0 disagreed" reads as an
                  alarm for the one state that is good news. */}
              <span className={partition[key] > 0 ? text : 'text-gray-400 dark:text-gray-500'}>
                {partition[key]} {label.toLowerCase()}
              </span>
            </React.Fragment>
          ))}
        {` of ${partition.stating} stated`}
      </span>
    </div>
  );
}

const isBoolFact = (value) => {
  if (typeof value === 'boolean') return true;
  const s = value == null ? '' : String(value).trim().toLowerCase();
  return s === 'true' || s === 'false';
};

// Render boolean-typed values (e.g. ground_truth = true / "false") as styled
// Yes/No chips so they read distinctly from free-text values like
// "specialty outdoor kitchen" — boolean facts and description facts shouldn't
// look the same in cells that are meant to compare them.
function GroundTruthValue({ value }) {
  if (value == null) {
    return <span className="italic text-gray-400 dark:text-gray-500">— no ground truth</span>;
  }
  if (isBoolFact(value)) {
    const truthy = (typeof value === 'boolean' ? value : String(value).trim().toLowerCase() === 'true');
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${
        truthy
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
          : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
      }`}>
        {truthy ? 'Yes' : 'No'}
      </span>
    );
  }
  return <span>{canonicalizeFactValue(value)}</span>;
}

// Lightweight edit-distance check used to catch likely typos before they
// persist to ground truth (markup-comment #64). Only flags small near-
// identical edits ("River" → "Riverr") — substantively different values
// are saved without nagging.
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  const dp = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }
  return dp[a.length][b.length];
}

function looksLikeTypo(original, edited) {
  const a = String(original ?? '').trim();
  const b = String(edited ?? '').trim();
  if (!a || !b || a === b) return false;
  // Both sides need to be word-like for a "typo" to make sense; short tokens
  // (yes/no, numbers) tend to be intentional replacements.
  if (a.length < 4 || b.length < 4) return false;
  return levenshtein(a, b) <= 2;
}

/* ── 6A Ground Truth (editable) ── */
function GroundTruth({ clientId, role }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Edits are keyed on `fact_key`, not `id`. `fact_key` is unique across the
  // fact set (56/56 in the 2026-08-10 capture) and is what
  // fn_update_aim_ground_truth keys on, so it is the canonical identifier here.
  // fn_get_aim_ground_truth does now return an `id` column — it did not when
  // this was written, and every row's `r.id` was `undefined`, which made
  // `editId === r.id` true for ALL rows at once and collided every React `key`.
  // Switching to `id` is possible but buys nothing while the write path keys on
  // `fact_key`.
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [pendingConfirmKey, setPendingConfirmKey] = useState(null);
  const [page, setPage] = useState(0);
  const { showSuccess, showError } = useNotification();
  const canEdit = role <= 2;

  // One category per page rather than a fixed row count: the bands are what
  // readers scan by, and Amenities alone runs to ~29 facts, which is what made
  // the single table hard to use. Ordered by the band label the user sees.
  const categoryPages = useMemo(() => {
    const grouped = rows.reduce((acc, r) => {
      (acc[r.fact_category] = acc[r.fact_category] || []).push(r);
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([cat, list]) => {
        // Backend ships singular band labels (e.g. "Amenity"); pluralize known
        // ones so the band reads as a section header.
        const bandRaw = list[0]?.fact_category_band_display || prettifyCategory(cat);
        return { cat, list, label: bandRaw === 'Amenity' ? 'Amenities' : bandRaw };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  // A new fetch can leave the viewer on a page that no longer exists.
  useEffect(() => { setPage(0); }, [rows]);
  const safePage = Math.min(page, Math.max(0, categoryPages.length - 1));
  const currentPage = categoryPages[safePage];

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const resp = await getAimGroundTruth(clientId);
      setRows(resp?.data || []);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Failed to load ground truth');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const saveEdit = async (factKey) => {
    setSavingKey(factKey);
    try {
      // updateAimGroundTruth takes (clientId, { factKey, factValue, ... }). The
      // previous call passed three positional args, so factKey/factValue both
      // destructured to undefined and every save sent nulls to the RPC.
      const current = rows.find((r) => r.fact_key === factKey);
      const resp = await updateAimGroundTruth(clientId, {
        factKey,
        factValue: draft,
        factCategory: current?.fact_category ?? null,
      });
      if (resp?.error) {
        const err = { message: resp?.message || 'Save did not return updated record.', status: 200 };
        throw err;
      }
      // Merge the draft optimistically, then overlay whatever the RPC echoed
      // back (its return shape is not part of the contract we can rely on).
      const echoed = resp?.data && typeof resp.data === 'object' && !Array.isArray(resp.data)
        ? resp.data
        : {};
      setRows((prev) => prev.map((r) => (
        r.fact_key === factKey ? { ...r, fact_value: draft, ...echoed } : r
      )));
      setEditKey(null);
      setDraft('');
      setPendingConfirmKey(null);
      showSuccess('Ground Truth Updated', 'Your change has been saved.');
    } catch (e) {
      console.error('Ground truth save failed:', e);
      const status = e?.status;
      const rawMsg = typeof e?.message === 'string' ? e.message : '';
      let msg;
      if (status === 403 || rawMsg.toLowerCase().includes('permission') || rawMsg.toLowerCase().includes('access denied')) {
        msg = 'You do not have permission to edit ground truth.';
      } else if (rawMsg) {
        msg = status ? `${rawMsg} (HTTP ${status})` : rawMsg;
      } else if (status) {
        msg = `Save failed (HTTP ${status}).`;
      } else {
        msg = 'Save failed. Check your connection and try again.';
      }
      setEditKey(null);
      setDraft('');
      setPendingConfirmKey(null);
      showError('Save Failed', msg);
    } finally {
      setSavingKey(null);
    }
  };

  // Intercept Save: always gate on an inline confirm before persisting a
  // ground-truth change. The confirm prompt makes accidental edits (typos
  // like "River" → "Riverr") obvious before they hit the database. Users
  // click "Save anyway" to commit. (Markup-comment #64.)
  const attemptSave = (factKey) => {
    if (pendingConfirmKey === factKey) {
      // Already confirmed — fall through and save.
      saveEdit(factKey);
      return;
    }
    setPendingConfirmKey(factKey);
  };

  if (loading) return <AimLoading height={180} message="Loading ground truth…" />;
  if (error) return <AimError message={error} onRetry={load} />;
  if (rows.length === 0) return <AimEmpty message="No ground truth facts recorded yet." />;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Ground Truth (6A)</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          The facts AI should know about your property. We score accuracy and detect hallucinations against this list. Click the pencil to correct a value if it drifts.
        </p>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {(currentPage ? [currentPage] : []).map(({ cat, list, label: bandDisplay }) => {
          return (
          <div key={cat}>
            <div className="text-sm font-semibold tracking-wide text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/40 px-4 py-2">{bandDisplay}</div>
            <div className="px-3 pb-3">
            <table className="w-full text-sm text-gray-900 dark:text-gray-100 table-fixed">
              <tbody>
                {list.map((r) => {
                  const inputId = `aim-gt-edit-${r.fact_key}`;
                  const isEditing = editKey === r.fact_key;
                  const isSaving = savingKey === r.fact_key;
                  return (
                  <tr key={r.fact_key} className={TABLE_ROW}>
                    <td className="py-2 pr-2 w-1/3 text-gray-700 dark:text-gray-300 break-words align-top">
                      {isEditing ? (
                        <label htmlFor={inputId} className="cursor-text">{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</label>
                      ) : (
                        prettifyAmenityLabel(r.fact_key_display || r.fact_key)
                      )}
                    </td>
                    <td className="py-2 pr-2 break-words align-top" colSpan={isEditing ? 2 : 1}>
                      {isEditing ? (
                        <div className="space-y-2">
                          {isBoolFact(r.fact_value) ? (
                            <select
                              id={inputId}
                              value={draft}
                              onChange={(e) => setDraft(e.target.value)}
                              disabled={isSaving}
                              className="w-full text-sm border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-60"
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          ) : (
                            <input
                              id={inputId}
                              type="text"
                              value={draft}
                              onChange={(e) => setDraft(e.target.value)}
                              disabled={isSaving}
                              className="w-full text-sm border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-60"
                            />
                          )}
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => attemptSave(r.fact_key)}
                              disabled={isSaving}
                              aria-busy={isSaving}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                              {isSaving && (
                                <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                                </svg>
                              )}
                              <span>{isSaving ? 'Saving…' : 'Save'}</span>
                            </button>
                            <button
                              onClick={() => { setEditKey(null); setDraft(''); setPendingConfirmKey(null); }}
                              disabled={isSaving}
                              className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                              Cancel
                            </button>
                          </div>
                          {pendingConfirmKey === r.fact_key && !isSaving && (
                            <GroundTruthConfirmModal
                              factLabel={prettifyAmenityLabel(r.fact_key_display || r.fact_key)}
                              originalValue={r.fact_value}
                              newValue={draft}
                              isTypoLikely={looksLikeTypo(r.fact_value, draft)}
                              onConfirm={() => attemptSave(r.fact_key)}
                              onCancel={() => setPendingConfirmKey(null)}
                            />
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-900 dark:text-white"><GroundTruthValue value={r.fact_value} /></span>
                      )}
                    </td>
                    {!isEditing && (
                      <td className="py-2 text-right w-10">
                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => {
                              const initial = r.fact_value == null ? '' : String(r.fact_value);
                              setDraft(isBoolFact(r.fact_value) ? initial.trim().toLowerCase() : initial);
                              setEditKey(r.fact_key);
                            }}
                            className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                            aria-label={`Edit ${prettifyAmenityLabel(r.fact_key_display || r.fact_key)}`}
                            title="Edit"
                          >
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              role="img"
                              aria-hidden="true"
                            >
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                            </svg>
                          </button>
                        ) : null}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
          );
        })}
      </div>
      <div className="px-4 pb-3">
        {/* One page per category, so totalItems is this band's fact count — the
            band header above names which one. */}
        <AimPagination
          page={safePage}
          pageCount={categoryPages.length}
          onPage={setPage}
          totalItems={currentPage ? currentPage.list.length : 0}
          label="fact"
        />
      </div>
    </div>
  );
}

const HALLUCINATIONS_PAGE_SIZE = 15;

// Markup-comment #66: each priority bucket (fabricated / cross-attribution /
// contradicts) gets the same header treatment — only the tone color changes
// so the visual hierarchy mirrors triage priority.
const HALLUCINATION_GROUP_TONES = {
  red: { text: 'text-red-700 dark:text-red-400' },
  orange: { text: 'text-orange-700 dark:text-orange-400' },
  amber: { text: 'text-amber-700 dark:text-amber-400' },
};

// Provider names for a hallucination row. The redesigned RPC returns them as a
// `providers[]` array (one row per distinct fact); older payloads had a single
// `display_name` per row. Normalizes both to an array.
function rowProviders(r) {
  if (Array.isArray(r.providers)) return r.providers;
  return r.display_name ? [r.display_name] : [];
}

function HallucinationGroup({ title, description, tone = 'red', cards, keyPrefix }) {
  const toneClass = (HALLUCINATION_GROUP_TONES[tone] || HALLUCINATION_GROUP_TONES.red).text;
  return (
    <div className="mb-4">
      <h4 className={`text-sm font-semibold ${toneClass}`}>
        {title}
      </h4>
      <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5 mb-3 leading-snug">
        {description}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {cards.map((r, i) => (
          <HallucinationCard key={`${keyPrefix}-${i}`} r={r} />
        ))}
      </div>
    </div>
  );
}

function HallucinationCard({ r }) {
  const [showAllPrompts, setShowAllPrompts] = useState(false);
  const [showAllValues, setShowAllValues] = useState(false);
  const [whyExpanded, setWhyExpanded] = useState(false);
  const type = classifyHallucinationType(r);
  const typeMeta = HALLUCINATION_TYPE_LABELS[type];
  const isContactFact = r.fact_category === 'contact';
  const visiblePrompts = showAllPrompts ? r.prompts : r.prompts?.slice(0, 2);
  const visibleValues = showAllValues ? r.statedValues : r.statedValues?.slice(0, 2);
  return (
    <div className={`rounded ${isContactFact ? 'border-l-[6px]' : 'border-l-4'} border-red-500 border border-red-200 dark:border-red-900 p-3 text-sm`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs uppercase tracking-wide text-red-700 dark:text-red-300">
        <span className="inline-flex items-center gap-x-2 gap-y-1 flex-wrap">
          {rowProviders(r).length > 0 ? (
            rowProviders(r).map((p, i) => (
              <span key={p || i} className="inline-flex items-center gap-1">
                <ProviderColorDot displayName={p} /> {prettifyProviderLabel(p)}
              </span>
            ))
          ) : (
            <span className="normal-case text-gray-400">Provider n/a</span>
          )}
        </span>
        <span className="inline-flex items-stretch gap-1.5">
          {isContactFact && (
            <HoverTooltip
              triggerClassName="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white normal-case cursor-help"
              content={(
                <span className="block">
                  This is flagged <strong>Critical</strong> because <strong>{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</strong> is guest-facing contact info — the most damaging kind of hallucination. AI answered <span className="text-amber-300">&ldquo;{prettifyBooleanValue(r.fact_value_stated) || '(not stated)'}&rdquo;</span> while your recorded ground truth is <span className="text-emerald-300">&ldquo;{r.ground_truth != null ? prettifyBooleanValue(r.ground_truth) : '—'}&rdquo;</span>. A wrong number or address sends guests to the wrong place, so fixing this takes priority over other facts.
                </span>
              )}
            >
              <svg
                className="w-3 h-3"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Critical
            </HoverTooltip>
          )}
          {r.groupCount > 1 && (
            <HoverTooltip
              triggerClassName="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-red-700 hover:bg-blue-700 text-white normal-case font-bold cursor-help transition-colors"
              content={(
                <>AI gave this same wrong answer across <strong className="text-amber-300">{r.groupCount}</strong> different queries this run — not a one-off slip. The more it repeats, the more guests are likely seeing it. Expand the queries list below to see exactly which prompts triggered it.</>
              )}
            >
              Repeated {r.groupCount}×
            </HoverTooltip>
          )}
          <HoverTooltip
            triggerClassName="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-200 text-red-900 dark:bg-red-900 dark:text-red-100 hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 normal-case cursor-help transition-colors"
            content={(
              <>
                {type === 'fabricated' && (
                  <span className="block">
                    This is flagged because, for <strong>{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</strong>, AI stated <span className="text-amber-300">&ldquo;{prettifyBooleanValue(r.fact_value_stated) || '(not stated)'}&rdquo;</span> even though you have no ground-truth value recorded for it. With nothing to check against, AI didn&rsquo;t draw this from your data — it invented the claim, which is why it counts as fabricated rather than simply wrong.
                  </span>
                )}
                {type === 'cross_attribution' && (
                  <span className="block">
                    This is flagged because AI described <strong>{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</strong> as <span className="text-amber-300">&ldquo;{prettifyBooleanValue(r.fact_value_stated) || '(not stated)'}&rdquo;</span>, but that detail matches a different property. The value isn&rsquo;t false in general — it&rsquo;s just attributed to the wrong place, which is why it&rsquo;s cross-attribution: AI confused you with a neighbor or a similarly-named campground.
                  </span>
                )}
                {type === 'contradicts' && (
                  <span className="block">
                    This is flagged because, for <strong>{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</strong>, AI answered <span className="text-amber-300">&ldquo;{prettifyBooleanValue(r.fact_value_stated) || '(not stated)'}&rdquo;</span> while your recorded ground truth is <span className="text-emerald-300">&ldquo;{r.ground_truth != null ? prettifyBooleanValue(r.ground_truth) : '—'}&rdquo;</span>. AI has the right fact on record but reported a different value, so the answer directly contradicts your ground truth.
                  </span>
                )}
              </>
            )}
          >
            {typeMeta.emoji} {typeMeta.label}
          </HoverTooltip>
        </span>
      </div>
      {r.groupCount > 1 ? (
        <div className="text-xs italic text-gray-700 dark:text-gray-300 mt-1">
          <div className="not-italic font-medium text-gray-600 dark:text-gray-400 mb-0.5">Queries ({r.prompts.length}):</div>
          <ul className="list-disc list-inside space-y-0.5">
            {visiblePrompts.map((p, idx) => (
              <li key={`${p.prompt}-${idx}`}>“{p.prompt}”</li>
            ))}
            {r.prompts.length > 2 && (
              <li className="not-italic list-none -ml-1">
                <button
                  type="button"
                  onClick={() => setShowAllPrompts((v) => !v)}
                  className="text-blue-600 dark:text-blue-400 hover:underline focus:outline-none focus:underline"
                >
                  {showAllPrompts ? 'Show less' : `+${r.prompts.length - 2} more`}
                </button>
              </li>
            )}
          </ul>
        </div>
      ) : (
        <div className="text-xs italic text-gray-700 dark:text-gray-300 mt-1">Query: “{r.prompt_text}”</div>
      )}
      {(() => {
        // Markup-comment #65: surface the type-mismatch / min-max-confusion
        // flags ONCE per fact (next to the fact name) instead of stamping
        // every value variant with the same badge. The flag applies to the
        // field's shape, not to any individual answer.
        const variants = (r.groupCount > 1 && Array.isArray(r.statedValues) && r.statedValues.length > 1)
          ? r.statedValues
          : [r.fact_value_stated];
        const hallucinationType = classifyHallucinationType(r);
        return (
          <>
            <div className="font-semibold text-red-900 dark:text-red-100 mt-1">{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</div>
            {variants.length > 1 ? (
              <div className="text-xs text-red-800 dark:text-red-200 mt-1">
                <div className="font-medium mb-0.5">AI said ({variants.length} variants):</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {visibleValues.map((v, idx) => (
                    <li key={`${v}-${idx}`}>“{prettifyBooleanValue(v)}”</li>
                  ))}
                  {variants.length > 2 && (
                    <li className="list-none -ml-1">
                      <button
                        type="button"
                        onClick={() => setShowAllValues((v) => !v)}
                        className="text-blue-600 dark:text-blue-400 hover:underline focus:outline-none focus:underline"
                      >
                        {showAllValues ? 'Show less' : `+${variants.length - 2} more`}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            ) : (
              <div className="text-xs text-red-800 dark:text-red-200">
                AI said: “{prettifyBooleanValue(r.fact_value_stated) || '(not stated)'}”
              </div>
            )}
            {r.ground_truth != null ? (
              <div className="text-xs text-red-800 dark:text-red-200 inline-flex items-center gap-1">
                Truth: <GroundTruthValue value={r.ground_truth} />
              </div>
            ) : (
              <div className="text-xs italic text-red-700 dark:text-red-300">Truth: FABRICATED — no ground-truth value</div>
            )}
          </>
        );
      })()}
    </div>
  );
}

/* ── 6B Hallucinations ── */
function Hallucinations({ clientId, runId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerFilter, setProviderFilter] = useState('');
  const [factFilter, setFactFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (!clientId || !runId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    setPage(0);
    // Provider/fact/type filters are applied client-side — the server
    // endpoint takes a provider id, but the UI lets users pick by display
    // name since that's what the rows expose.
    getAimHallucinations(clientId, { runId })
      .then((resp) => { if (!cancelled) setRows(resp?.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, runId]);

  // Reset to page 0 when filters narrow the result set, otherwise the
  // user can land on a stale empty page.
  useEffect(() => { setPage(0); }, [providerFilter, factFilter, typeFilter]);

  const providerOptions = useMemo(
    // Redesigned RPC lists providers per fact in `providers[]`; older payloads
    // had a single `display_name` per row. Support both.
    () => Array.from(new Set(rows.flatMap(rowProviders).filter(Boolean))).sort(),
    [rows],
  );
  // Map fact_key → fact_key_display so the filter dropdown can render the
  // canonical label that the backend already produced.
  const factOptions = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (!r.fact_key) continue;
      if (!map.has(r.fact_key)) map.set(r.fact_key, prettifyAmenityLabel(r.fact_key_display || r.fact_key));
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  // The type dropdown also exposes a "repeated" pseudo-type that filters
  // grouped cards by groupCount > 1 — handle it separately from the real
  // hallucination types because it's a property of the group, not the row.
  const isRealType = typeFilter && typeFilter !== 'repeated';
  const filteredRows = useMemo(() => rows.filter((r) => {
    if (providerFilter && !rowProviders(r).includes(providerFilter)) return false;
    if (factFilter && r.fact_key !== factFilter) return false;
    if (isRealType && classifyHallucinationType(r) !== typeFilter) return false;
    return true;
  }), [rows, providerFilter, factFilter, isRealType, typeFilter]);

  // Group rows that report the same wrong fact (same provider + fact_key).
  // The AI may phrase the same hallucination slightly differently each run
  // (e.g., "2026 Campspot Awards Winner" vs "2026 Campspot Awards winner"),
  // so the fact_value is tracked separately, not used as part of the key.
  const groupedRows = useMemo(() => {
    const map = new Map();
    for (const r of filteredRows) {
      // Redesigned RPC already returns one row per distinct fact (with a
      // providers[] list and an occurrences count), so key by fact_key. Older
      // per-instance payloads fall back to display_name||fact_key.
      const key = r.fact_key ? `${r.fact_key}` : `${r.display_name}||${r.fact_value_stated}`;
      if (!map.has(key)) {
        map.set(key, {
          ...r,
          providers: rowProviders(r),
          prompts: r.prompt_text ? [{ prompt: r.prompt_text, value: r.fact_value_stated }] : [],
          statedValues: r.fact_value_stated ? [r.fact_value_stated] : [],
          // Occurrences from the new shape; count rows for the legacy shape.
          groupCount: Number(r.occurrences ?? 1),
        });
      } else {
        const g = map.get(key);
        g.groupCount += Number(r.occurrences ?? 1);
        for (const p of rowProviders(r)) {
          if (!g.providers.includes(p)) g.providers.push(p);
        }
        if (r.prompt_text) g.prompts.push({ prompt: r.prompt_text, value: r.fact_value_stated });
        if (r.fact_value_stated && !g.statedValues.includes(r.fact_value_stated)) {
          g.statedValues.push(r.fact_value_stated);
        }
      }
    }
    const all = Array.from(map.values());
    if (typeFilter === 'repeated') return all.filter((g) => g.groupCount > 1);
    return all;
  }, [filteredRows, typeFilter]);

  const providerCount = useMemo(() => new Set(filteredRows.flatMap(rowProviders)).size, [filteredRows]);
  const insight = buildHallucinationWarning(filteredRows.length, providerCount);

  // Markup-comment #66: split hallucinations by priority so the user can
  // triage top-down — fabricated facts first (AI invented a claim with no
  // ground truth), then cross-attribution (someone else's fact attributed
  // to you), then plain contradictions of recorded values.
  const { fabricatedGroups, crossAttribGroups, contradictsGroups } = useMemo(() => {
    const fab = [];
    const cross = [];
    const contradicts = [];
    for (const g of groupedRows) {
      const type = classifyHallucinationType(g);
      if (type === 'fabricated') fab.push(g);
      else if (type === 'cross_attribution') cross.push(g);
      else contradicts.push(g);
    }
    return { fabricatedGroups: fab, crossAttribGroups: cross, contradictsGroups: contradicts };
  }, [groupedRows]);
  const restGroups = useMemo(() => [...crossAttribGroups, ...contradictsGroups], [crossAttribGroups, contradictsGroups]);

  const pageCount = Math.max(1, Math.ceil(contradictsGroups.length / HALLUCINATIONS_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * HALLUCINATIONS_PAGE_SIZE;
  const visibleContradicts = contradictsGroups.slice(pageStart, pageStart + HALLUCINATIONS_PAGE_SIZE);

  const selectClass = 'text-xs border dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white max-[543px]:w-[calc(50%-0.25rem)] max-[379px]:w-full';

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Hallucinations (6B)</h3>
          <div className="flex flex-wrap gap-2 max-[543px]:w-full">
            <select
              aria-label="Filter by provider"
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">All providers</option>
              {providerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              aria-label="Filter by fact"
              value={factFilter}
              onChange={(e) => setFactFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">All facts</option>
              {factOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
            <select
              aria-label="Filter by type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">All types</option>
              {Object.entries(HALLUCINATION_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
              <option value="repeated">Repeated</option>
            </select>
          </div>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-2">
          Specific factual claims AI made that don&apos;t match your ground truth. Filter by provider or fact to triage the worst offenders.
        </p>
        {loading ? <AimLoading height={120} /> : filteredRows.length === 0 ? (
          <AimEmpty message={rows.length === 0 ? 'No hallucinations detected. ✅' : 'No hallucinations match these filters.'} />
        ) : (
          <>
            {fabricatedGroups.length > 0 && (
              <HallucinationGroup
                title={`Fabricated facts (${fabricatedGroups.length}) — Fix First`}
                description="AI invented these claims about your property and there's no ground-truth value to score them against (e.g., a made-up award win or amenity). These are the highest-impact hallucinations — they spread misinformation guests act on. Add a ground-truth answer for each so future runs can flag re-occurrences."
                tone="red"
                cards={fabricatedGroups}
                keyPrefix="fab"
              />
            )}
            {crossAttribGroups.length > 0 && (
              <HallucinationGroup
                title={`Cross-attribution (${crossAttribGroups.length}) — Review Next`}
                description="AI mixed you up with another property — these statements describe someone else's amenities, awards, or location but were attributed to you. Fix the prompts or ground truth so AI stops borrowing facts from neighbors."
                tone="orange"
                cards={crossAttribGroups}
                keyPrefix="cross"
              />
            )}
            {contradictsGroups.length > 0 && (
              <HallucinationGroup
                title={`Contradicts ground truth (${contradictsGroups.length}) — Clean Up`}
                description="AI knows your ground truth but reported a different value. Lower stakes than fabrication, but still misleading — review the variants below and consider strengthening the prompt wording."
                tone="amber"
                cards={visibleContradicts}
                keyPrefix="con"
              />
            )}
          </>
        )}
        {!loading && filteredRows.length > 0 && (
          <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-400 max-[499px]:flex-col max-[499px]:items-stretch max-[499px]:gap-3">
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span>
                {filteredRows.length} hallucination{filteredRows.length === 1 ? '' : 's'}
                {groupedRows.length !== filteredRows.length && ` (${groupedRows.length} unique fact${groupedRows.length === 1 ? '' : 's'})`}
              </span>
            </span>
            <nav aria-label="Hallucinations pagination" className="flex items-center gap-1 max-[499px]:w-full max-[499px]:justify-between">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800/60 max-[499px]:px-3"
              >
                Previous
              </button>
              {buildPageRange(safePage, pageCount).map((p, i) => (
                p === '…' ? (
                  <span key={`ellipsis-${i}`} className="px-1 text-gray-400 max-[499px]:hidden" aria-hidden="true">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    aria-current={p === safePage ? 'page' : undefined}
                    className={`min-w-[28px] px-2 py-1 rounded border text-xs max-[499px]:hidden ${
                      p === safePage
                        ? 'border-[#32B1FF] bg-[#32B1FF] text-white'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60'
                    }`}
                  >
                    {p + 1}
                  </button>
                )
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={safePage >= pageCount - 1}
                className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800/60 max-[499px]:px-3"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>
      {insight && <InsightCard {...insight} />}
    </div>
  );
}

/* ── 6C Provider Consensus ── */
function ProviderConsensus({ clientId, runId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  // Only rows that name a fact belong in a per-fact table; anything else says
  // nothing about any fact and would render as a blank row with 0% consensus.
  const factRows = useMemo(
    () => rows.filter((r) => r && typeof r === 'object' && r.fact_key),
    [rows],
  );
  const { page, setPage, pageCount, visibleRows, totalItems } = usePagedRows(factRows);

  useEffect(() => {
    let cancelled = false;
    if (!clientId || !runId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    setExpanded(new Set());
    getAimConsensusDetail(clientId, { runId })
      .then((resp) => { if (!cancelled) setRows(resp?.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, runId]);

  if (loading) return <AimLoading height={160} />;
  if (factRows.length === 0) return <AimEmpty message="No consensus data for this run." />;

  const toggleExpand = (factKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(factKey)) next.delete(factKey); else next.add(factKey);
      return next;
    });
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Provider Consensus (6C)</h3>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
        Where AI providers agree or disagree about your property. Consensus below 50% usually means your public-facing info is inconsistent across sources, not that one provider is wrong.
        {' '}A <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded text-yellow-500 dark:text-yellow-400 font-medium align-middle">CONTRADICTS</span> label next to a value means the row needs your attention — either the ground truth is in the wrong format for the question (yes/no fact stored as free text) or there&rsquo;s no ground truth recorded yet and AI scoring is falling back to provider consensus. Hover the pill for the specific reason and what to amend.
      </p>
      <div className={`${TABLE_FRAME} responsive-card-table-wrapper`}>
      <table className="w-full text-sm text-gray-900 dark:text-gray-100 min-w-[640px] responsive-card-table">
        <thead className={TABLE_THEAD}>
          <tr>
            <th className="text-left px-3 py-2.5 whitespace-nowrap align-bottom">Fact</th>
            <th className="text-left px-3 py-2.5 whitespace-nowrap align-bottom">Ground Truth</th>
            <th className="text-left px-3 py-2.5 whitespace-nowrap min-w-[200px] align-bottom">
              <div>Providers</div>
              <div className="mt-0.5 flex items-center gap-2 text-[10px] font-normal normal-case tracking-normal flex-wrap">
                {CONSENSUS_SEGMENTS.map(({ key, label, swatch }) => (
                  <span key={key} className="inline-flex items-center gap-1">
                    <span aria-hidden="true" className={`inline-block w-1.5 h-1.5 rounded-sm ${swatch}`} />{label}
                  </span>
                ))}
              </div>
              <div className="mt-0.5 text-[10px] font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">
                Each provider counts once. Mixed = its statements disagree with each other, and still counts toward Consensus.
              </div>
            </th>
            <th className="text-right px-3 py-2.5 whitespace-nowrap align-bottom">Consensus</th>
            {/* An empty column with no explanation reads as broken — which is
                how this was reported. The header says what would put a badge
                here, so silence is legible as "nothing needs attention". */}
            <th className="text-right px-3 py-2.5 whitespace-nowrap align-bottom">
              <HoverTooltip
                triggerClassName="border-b border-dotted border-gray-400 dark:border-gray-500 cursor-help"
                content={(
                  <>
                    <strong>Flag.</strong> Marks the facts worth acting on, and stays empty when none are.
                    <br /><br />
                    <span className="text-red-300"><strong>❗ Priority</strong></span> — guest-facing contact info (address, phone, email) under {CONSENSUS_FLAG_THRESHOLD}% consensus. A wrong number or address sends guests to the wrong place, so these come first.
                    <br /><br />
                    <span className="text-amber-300"><strong>⚠ Review</strong></span> — any other fact under {CONSENSUS_FLAG_THRESHOLD}% consensus. Usually your public-facing information is inconsistent across sources rather than one provider being wrong.
                    <br /><br />
                    Facts nobody stated are never flagged — an unmeasured fact is not a disagreement.
                  </>
                )}
              >
                Flag
              </HoverTooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r) => {
            // Null consensus means nobody stated the fact, not that everybody
            // disagreed — it must not render as a red 0% or earn a priority flag.
            const statingCount = Number(r.providers_stating ?? 0);
            // Null consensus, or nobody stating, means "not measurable" — not
            // that everybody disagreed. It must not render as a red 0% or earn
            // a priority flag.
            const consensus = r.consensus_pct == null || statingCount === 0
              ? null
              : Number(r.consensus_pct);
            // Any fact the providers mostly disagree on is worth surfacing —
            // until 2026-08-11 this fired only on `contact`, so a fact AI got
            // wrong 86% of the time (has_kayaking, 14%) showed nothing and the
            // column read as broken. Contact keeps the louder treatment: a
            // wrong phone number or address sends guests to the wrong place.
            const lowConsensus = consensus != null && consensus < CONSENSUS_FLAG_THRESHOLD;
            const flag = !lowConsensus
              ? null
              : (r.fact_category === 'contact' ? FACT_FLAGS.priority : FACT_FLAGS.review);
            const { providers: wrongProviders, values: wrongValues } = consensusDisagreements(r);
            const partition = consensusProviderPartition(r);
            const isExpanded = expanded.has(r.fact_key);
            const canExpand = wrongProviders.length > 0 || wrongValues.length > 0;
            return (
              <React.Fragment key={r.fact_key}>
                <tr
                  className={`${TABLE_ROW} rc-cells-top ${canExpand ? TABLE_ROW_CLICKABLE : ''} ${isExpanded ? '' : 'rc-collapsed'}`}
                  {...(canExpand ? clickableRowProps(() => toggleExpand(r.fact_key)) : {})}
                  aria-expanded={canExpand ? isExpanded : undefined}
                >
                  <td className={`px-3 py-3 align-top rc-title-cell max-[499px]:!text-left ${isExpanded ? 'rc-title-open' : ''}`} data-label="Fact">
                    <div className="font-medium text-gray-900 dark:text-white inline-flex items-center gap-1.5 text-left">
                      {canExpand
                        ? <span aria-hidden="true" className="text-gray-500 dark:text-gray-400 select-none">{isExpanded ? '▾' : '▸'}</span>
                        : <span aria-hidden="true" className="inline-block w-[10px]" />}
                      <span>{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</span>
                    </div>
                    {r.fact_category && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 pl-[18px] text-left">{r.fact_category_display || prettifyCategory(r.fact_category)}</div>
                    )}
                  </td>
                  <td
                    className="py-2 text-gray-700 dark:text-gray-300 max-w-[260px]"
                    data-label="Ground Truth"
                  >
                    {r.ground_truth != null ? (
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        <GroundTruthValue value={r.ground_truth} />
                        {isTypeMismatch(r.fact_key, r.ground_truth) && (
                          <HoverTooltip
                            triggerClassName="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded text-yellow-500 dark:text-yellow-400 hover:text-blue-500 dark:hover:text-blue-400 normal-case cursor-help transition-colors font-medium"
                            content={(
                              <>
                                <strong>Format mismatch.</strong> This is a yes/no fact, but your recorded value <span className="text-amber-300">&ldquo;{prettifyBooleanValue(r.ground_truth)}&rdquo;</span> is free text. AI can&rsquo;t reliably compare its answer against this kind of value.
                                <br /><br />
                                <span className="text-emerald-300"><strong>Amendment needed:</strong></span> change the ground truth to a true <span className="text-emerald-300">Yes/No</span> value so future runs score accurately.
                              </>
                            )}
                          >
                            · CONTRADICTS
                          </HoverTooltip>
                        )}
                      </span>
                    ) : r.modal_value != null ? (
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        <GroundTruthValue value={r.modal_value} />
                        <HoverTooltip
                          triggerClassName="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded text-yellow-500 dark:text-yellow-400 hover:text-blue-500 dark:hover:text-blue-400 normal-case cursor-help transition-colors font-medium"
                          content={(
                            <>
                              <strong>No ground truth recorded.</strong> The value shown is a <span className="text-amber-300">modal value</span> — the answer most providers gave (<span className="text-amber-300">&ldquo;{prettifyBooleanValue(r.modal_value)}&rdquo;</span>) — used as a fallback so the row isn&rsquo;t empty.
                              {isTypeMismatch(r.fact_key, r.modal_value) && (
                                <>
                                  <br /><br />
                                  <strong>Format mismatch too.</strong> Even as a fallback, this is a yes/no fact and the value is free text, so AI&rsquo;s answers still can&rsquo;t be compared cleanly.
                                </>
                              )}
                              <br /><br />
                              <span className="text-emerald-300"><strong>Amendment needed:</strong></span> add a real ground-truth value for <strong>{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</strong> so accuracy scoring isn&rsquo;t guessing from provider consensus.
                            </>
                          )}
                        >
                          · CONTRADICTS
                        </HoverTooltip>
                      </span>
                    ) : (
                      <span className="italic text-gray-400 dark:text-gray-500">— no value</span>
                    )}
                  </td>
                  <td className="px-3 py-3 pr-3" data-label="Providers">
                    <ProviderPartitionCell partition={partition} statingCount={statingCount} />
                  </td>
                  <td className={`px-3 py-3 text-right tabular-nums ${consensus != null && consensus < 50 ? 'text-red-600 dark:text-red-400' : ''}`} data-label="Consensus">
                    {consensus == null
                      ? <span className="text-gray-400 dark:text-gray-500" title="No provider stated this fact in this run, so consensus is not measurable.">—</span>
                      : `${consensus.toFixed(0)}%`}
                  </td>
                  <td className="px-3 py-3 text-right" data-label="Flag">
                    {flag && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${flag.className}`}
                        title={flag.reason}
                      >
                        <span aria-label={flag.key} className="mr-1">{flag.icon}</span>{flag.label}
                      </span>
                    )}
                  </td>
                </tr>
                {isExpanded && canExpand && (
                  <tr className={`${TABLE_ROW} bg-gray-50/60 dark:bg-gray-900/60`}>
                    <td colSpan={5} className="py-2 pl-[18px] pr-3 text-xs space-y-1">
                      <div className="space-y-1" style={{ textAlign: 'left' }}>
                      {wrongProviders.length > 0 && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Wrong: </span>
                          <span className="text-red-700 dark:text-red-300">{wrongProviders.join(', ')}</span>
                        </div>
                      )}
                      {wrongValues.length > 0 && (
                        <div className="pt-3">
                          <span className="text-gray-500 dark:text-gray-400">Said: </span>
                          <span className="text-red-700 dark:text-red-300">
                            {wrongValues.map((v, idx) => (
                              <React.Fragment key={`${v}-${idx}`}>
                                {idx > 0 && ', '}
                                “{v}”
                              </React.Fragment>
                            ))}
                          </span>
                        </div>
                      )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
      <AimPagination
        page={page}
        pageCount={pageCount}
        onPage={setPage}
        totalItems={totalItems}
        label="fact"
      />
    </div>
  );
}

/* ── 6D Knowledge Recency ── */
function KnowledgeRecency({ clientId }) {
  // The freshness gap comes from the P12 snapshot rather than the P12 series in
  // fn_get_aim_snapshot_trend: the card shows one number, and the snapshot RPC
  // returns exactly that. Reading the trend would mean fetching the whole series
  // to take its last row.
  const { data: snap, loading: lSnap } = useAimSnapshot(clientId, 'P12', '');
  const { data: timeline, loading: lTl } = useAimKnowledgeRecencyTimeline(clientId);
  // Pull ground-truth changes so we can flag fact_values whose most recent
  // edit looks like a typo (e.g., Verde River → Verde Riverr). The
  // KnowledgeRecency table is where someone reads the current value, so the
  // typo flag lives here rather than buried in the changes-history table.
  const [changes, setChanges] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!clientId) { setChanges([]); return; }
    getAimGroundTruthChanges(clientId, 50)
      .then((resp) => { if (!cancelled) setChanges(resp?.data || []); })
      .catch(() => { if (!cancelled) setChanges([]); });
    return () => { cancelled = true; };
  }, [clientId]);
  const typoFactKeys = useMemo(() => {
    const seen = new Set();
    const out = new Map();
    for (const c of changes) {
      if (seen.has(c.fact_key)) continue;
      seen.add(c.fact_key);
      if (isLikelyTypoEdit(c.old_value, c.new_value)) {
        out.set(c.fact_key, { oldValue: c.old_value, newValue: c.new_value });
      }
    }
    return out;
  }, [changes]);

  const days = Number(snap?.value_numeric ?? 0);
  const daysBehind = Math.round(days);

  if (lSnap || lTl) return <AimLoading height={200} />;
  const view = knowledgeRecencyView(timeline);
  if (view.mode === 'empty') return <AimEmpty message="No knowledge recency data." />;
  const { known, unknown } = view;

  const fmtDate = (r) => {
    const raw = r.effective_date || r.created_at;
    if (!raw) return 'unknown';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return 'unknown';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const knownByDate = [...known].sort((a, b) => {
    const ta = toEpoch(a) ?? 0;
    const tb = toEpoch(b) ?? 0;
    return tb - ta;
  });
  const newestKnownDate = knownByDate[0] ? fmtDate(knownByDate[0]) : null;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="flex flex-col gap-1 mb-3 min-[560px]:flex-row min-[560px]:items-baseline min-[560px]:justify-between min-[560px]:gap-0">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {'Knowledge Recency (6D / '}
            <InfoTooltip
              label="P12 — Knowledge Recency"
              trigger={<span className="border-b border-dotted border-gray-400 dark:border-gray-500 cursor-help">P12</span>}
            >
              <strong>P12 — Knowledge Recency.</strong> Whether AI is current on your property. Lists the ground-truth
              facts AI correctly knows, newest first, alongside the facts you've
              recorded that AI hasn't picked up yet. Use it to spot stale gaps.
            </InfoTooltip>
            {')'}
          </h3>
        </div>
        {snap?.value_numeric != null && (
          <span className="self-start min-[560px]:self-auto text-xs text-gray-600 dark:text-gray-400 inline-flex items-center gap-1">
            Freshness gap: <span className="font-semibold text-gray-900 dark:text-white">{formatDaysBehind(daysBehind)}</span>
            <InfoTooltip label="Freshness gap" align="responsive">
              Reference point: <strong>today's date</strong>. Measures the gap
              between today and the <strong>effective date</strong> of the most
              recent ground-truth fact AI gets right
              {newestKnownDate ? <> (currently <strong>{newestKnownDate}</strong>)</> : null}. Lower is better — a high number means AI is missing your
              latest property updates.
            </InfoTooltip>
          </span>
        )}
      </div>
      {knownByDate.length > 0 && (() => {
        const visibleKnown = knownByDate.slice(0, 5);
        return (
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Last updated ({visibleKnown.length} of {knownByDate.length} facts AI knows)</div>
            <div className={`${TABLE_FRAME} responsive-card-table-wrapper`}>
              <table className="w-full text-sm min-w-[480px] responsive-card-table">
                <thead className={TABLE_THEAD}>
                  <tr>
                    <th scope="col" className="w-6 py-1 pr-2" aria-label="Status" />
                    <th scope="col" className="text-left py-1 pr-3 font-semibold">Fact</th>
                    <th scope="col" className="text-left py-1 pr-3 font-semibold">Value</th>
                    <th scope="col" className="text-right py-1 font-semibold">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleKnown.map((r, i) => {
                    const typo = typoFactKeys.get(r.fact_key);
                    return (
                    <tr key={`k-${r.fact_key}-${i}`} className={`${TABLE_ROW} align-top`}>
                      <td className="px-3 py-2.5 pr-2" data-label="Status"><span aria-hidden="true">🟢</span></td>
                      <td className="px-3 py-2.5 pr-3 font-medium text-emerald-700 dark:text-emerald-400 whitespace-nowrap" data-label="Fact">{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</td>
                      <td className="px-3 py-2.5 pr-3 text-gray-700 dark:text-gray-300 break-words" data-label="Value">
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          <span>{prettifyBooleanValue(r.fact_value) || <span className="text-gray-400">—</span>}</span>
                          {typo && (
                            <HoverTooltip
                              triggerClassName="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded text-amber-600 dark:text-amber-300 hover:text-blue-500 dark:hover:text-blue-400 normal-case cursor-help transition-colors font-semibold tracking-wide"
                              content={(
                                <>
                                  <strong>Likely typo detected.</strong> A recent ground-truth edit changed <span className="text-red-300">&ldquo;{typo.oldValue}&rdquo;</span> to <span className="text-amber-300">&ldquo;{typo.newValue}&rdquo;</span> — the two differ by a single character, which is the classic typo signature.
                                  <br /><br />
                                  <span className="text-emerald-300"><strong>Amendment needed:</strong></span> if the new value is intentional, leave it; otherwise revert to <span className="text-red-300">&ldquo;{typo.oldValue}&rdquo;</span> so AI scoring isn&rsquo;t penalized by a misspelled ground truth.
                                </>
                              )}
                            >
                              · TYPO ERROR
                            </HoverTooltip>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap" data-label="Updated">{fmtDate(r)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
      {unknown.length > 0 && (() => {
        const sortedUnknown = [...unknown].sort((a, b) => {
          const ca = (a.fact_category || '').toString();
          const cb = (b.fact_category || '').toString();
          if (ca !== cb) return ca.localeCompare(cb);
          return (a.fact_key || '').localeCompare(b.fact_key || '');
        });
        const visible = sortedUnknown.slice(0, 10);
        return (
          <div className="mt-3">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">AI doesn't know ({visible.length} of {unknown.length} facts)</div>
            <div className={`${TABLE_FRAME} responsive-card-table-wrapper`}>
              <table className="w-full text-sm min-w-[480px] responsive-card-table">
                <thead className={TABLE_THEAD}>
                  <tr>
                    <th scope="col" className="w-6 py-1 pr-2" aria-label="Status" />
                    <th scope="col" className="text-left py-1 pr-3 font-semibold">Fact</th>
                    <th scope="col" className="text-left py-1 pr-3 font-semibold">Value</th>
                    <th scope="col" className="text-left py-1 font-semibold">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.fact_key} className={`${TABLE_ROW} align-top`}>
                      <td className="px-3 py-2.5 pr-2" data-label="Status"><span aria-hidden="true">🔴</span></td>
                      <td className="px-3 py-2.5 pr-3 font-medium text-red-700 dark:text-red-400 whitespace-nowrap" data-label="Fact">{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</td>
                      <td className="px-3 py-2.5 pr-3 text-gray-700 dark:text-gray-300 break-words" data-label="Value">{prettifyBooleanValue(r.fact_value) || <span className="text-gray-400">—</span>}</td>
                      <td className="px-3 py-2.5" data-label="Category">
                        {r.fact_category ? (
                          <span className="inline-block text-[10px] tracking-wide font-medium px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 whitespace-nowrap">
                            {(r.fact_category_display || prettifyCategory(r.fact_category))}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ── 6E Category Confusion ── */
function CategoryConfusion({ clientId, runId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!clientId || !runId) { setData(null); setLoading(false); return; }
    setLoading(true);
    getAimCategoryConfusion(clientId, runId)
      .then((resp) => { if (!cancelled) setData(resp?.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, runId]);

  if (loading) return <AimLoading height={180} />;
  const breakdown = Array.isArray(data?.breakdown) ? data.breakdown : [];
  if (breakdown.length === 0) return <AimEmpty message="No category-confusion data for this run." />;
  const confusionIndex = data?.confusion_index ?? null;
  const groundTruth = data?.ground_truth_property_type;
  const groundTruthDisplay = data?.ground_truth_property_type_display || groundTruth;
  const staleness = categoryConfusionStaleness(data);
  const stalenessDetail = categoryConfusionStaleDetail(staleness, data);
  const top3 = breakdown.slice(0, 3);
  const COLORS = ['#10B981', '#F59E0B', '#EF4444', '#6366F1', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Category Confusion (6E)</h3>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
        Where AI mis-categorizes your property. Compares the category you&apos;ve declared in ground truth vs the category AI most often describes you as.
      </p>
      <div className="relative" style={{ height: 240 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={breakdown} dataKey="count" nameKey="category" innerRadius={60} outerRadius={90}>
              {breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <ThemedChartTooltip />
          </PieChart>
        </ResponsiveContainer>
        {confusionIndex != null && (() => {
          const ci = Number(confusionIndex);
          return (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{ci.toFixed(2)}</div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{confusionTier(ci)}</div>
            </div>
          );
        })()}
      </div>
      {groundTruth && (
        <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
          <span className="font-semibold text-gray-900 dark:text-white">You ARE:</span> {groundTruthDisplay}
          {staleness && (
            <span
              className="ml-1 text-[10px] uppercase tracking-wide font-semibold text-amber-700 dark:text-amber-300"
              title={stalenessDetail}
            >
              ({staleness.label})
            </span>
          )}.
          {top3.length > 0 && <> <span className="font-semibold text-gray-900 dark:text-white">AI calls you:</span> {top3.map((c) => `${c.category_display || prettifyCategory(c.category)} ${Number(c.pct).toFixed(1)}%`).join(', ')}.</>}
        </p>
      )}
    </div>
  );
}

function fmtChangeDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

/* ── 6F Ground Truth Changes ── */
function GroundTruthChanges({ clientId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!clientId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    getAimGroundTruthChanges(clientId, 50)
      .then((resp) => { if (!cancelled) setRows(resp?.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) return <AimLoading height={120} />;
  if (rows.length === 0) return <AimEmpty message="No ground-truth changes recorded." />;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Ground Truth Changes (6F)</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          Facts where AI&apos;s most-mentioned value drifted from a previous run. Use it to catch outdated information AI hasn&apos;t refreshed yet.
        </p>
      </div>
      <div className={`${TABLE_FRAME_FLUSH} responsive-card-table-wrapper`}>
      <table className="responsive-card-table w-full text-sm text-gray-900 dark:text-gray-100 min-w-[640px]">
        <thead className={TABLE_THEAD}>
          <tr>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Fact</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">From</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">To</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={TABLE_ROW}>
              <td data-label="Fact" className="px-4 py-3 text-gray-900 dark:text-white">{prettifyAmenityLabel(r.fact_key_display || r.fact_key)}</td>
              <td data-label="From" className="px-4 py-3 text-gray-600 dark:text-gray-400 line-through">{prettifyBooleanValue(r.old_value) || '—'}</td>
              <td data-label="To" className="px-4 py-3 text-emerald-700 dark:text-emerald-400">{prettifyBooleanValue(r.new_value) || '—'}</td>
              <td data-label="Updated" className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtChangeDate(r.changed_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default function DiagnosticsTab({ clientId, runId }) {
  const role = useActiveRole().role;
  return (
    <div className="space-y-5">
      <GroundTruth clientId={clientId} role={role} />
      <Hallucinations clientId={clientId} runId={runId} />
      <ProviderConsensus clientId={clientId} runId={runId} />
      <KnowledgeRecency clientId={clientId} />
      <CategoryConfusion clientId={clientId} runId={runId} />
      <GroundTruthChanges clientId={clientId} />
    </div>
  );
}

// WCAG 2.1 AA-compliant confirmation modal shown before a ground-truth edit
// is persisted (markup-comment #64). Mirrors the ShowRoutesConfirmModal
// pattern in MapOriginsTab: role="alertdialog" + aria-modal, focus trap,
// Escape + backdrop click cancel, initial focus on the primary action.
function GroundTruthConfirmModal({ factLabel, originalValue, newValue, isTypoLikely, onConfirm, onCancel }) {
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const titleId = 'aim-gt-confirm-title';
  const descId = 'aim-gt-confirm-desc';

  useFocusTrap(dialogRef);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      ref={dialogRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        onClick={onCancel}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative w-full max-w-md rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl">
        <div className="px-5 pt-5 pb-3">
          <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white">
            {isTypoLikely ? 'Looks like a typo — confirm save?' : 'Confirm Ground-Truth update'}
          </h2>
          <div id={descId} className="mt-2 space-y-3 text-sm text-gray-600 dark:text-gray-300">
            <p>
              Are you sure you want to update <strong className="text-gray-900 dark:text-white">{factLabel}</strong>?
              This becomes the source of truth AI is measured against, so a stray character can flip future accuracy scores.
            </p>
            <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 text-xs space-y-1">
              <div>
                <span className="text-gray-500 dark:text-gray-400">From:</span>{' '}
                <span className="font-mono text-gray-900 dark:text-gray-100 break-all">&ldquo;{String(originalValue ?? '')}&rdquo;</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">To:</span>{' '}
                <span className="font-mono text-gray-900 dark:text-gray-100 break-all">&ldquo;{String(newValue ?? '')}&rdquo;</span>
              </div>
            </div>
            {isTypoLikely && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Heads up — the new value is just a few characters off from the old one, which is the classic typo signature.
              </p>
            )}
          </div>
        </div>
        <div className="px-5 pb-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
          >
            Keep editing
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-md bg-[#32B1FF] text-sm font-medium text-white hover:bg-[#1a9ce6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
